import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  Alert,
  Collapse,
} from "@mui/material";

import axiosApi from "../Utils/axiosApi";

const StudentHostlerUpdate = ({ open, hosteler, onClose }) => {
  // -------------------- FORM DATA --------------------
  const [formData, setFormData] = useState({
    email: "",
    countryId: "",
    stateId: "",
    districtId: "",
    cityId: "",
    address: "",
    guardianName: "",
    guardianContactNumber: "",
    dob: "",
  });

  // -------------------- DROPDOWN LISTS --------------------
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  // -------------------- BANNER MESSAGE --------------------
  const [banner, setBanner] = useState({
    open: false,
    type: "success",
    message: "",
  });

  // -------------------- VALIDATION ERRORS --------------------
  const [errors, setErrors] = useState({
    email: "",
    guardianContactNumber: "",
  });

  // -------------------- HANDLE CHANGE (single handler) --------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    // When parent selects change, clear dependent fields:
    if (name === "countryId") {
      setFormData((prev) => ({
        ...prev,
        countryId: value,
        stateId: "",
        districtId: "",
        cityId: "",
      }));
      return;
    }

    if (name === "stateId") {
      setFormData((prev) => ({
        ...prev,
        stateId: value,
        districtId: "",
        cityId: "",
      }));
      return;
    }

    if (name === "districtId") {
      setFormData((prev) => ({
        ...prev,
        districtId: value,
        cityId: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -------------------- LOAD COUNTRY LIST --------------------
  useEffect(() => {
    axiosApi
      .post("/country/getCountry", {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.countries || [];
        setCountries(data);
      })
      .catch((err) => {
        console.error("Failed to load countries", err);
      });
  }, []);

  // -------------------- LOAD STATES WHEN COUNTRY CHANGES --------------------
  useEffect(() => {
    const countryId = formData.countryId;
    if (!countryId) {
      setStates([]);
      return;
    }

    axiosApi
      .post("/state/getState", { countryIds: [countryId] })
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.states || [];
        setStates(data);
      })
      .catch((err) => {
        console.error("Failed to load states", err);
        setStates([]);
      });
  }, [formData.countryId]);

  // -------------------- LOAD DISTRICTS WHEN STATE CHANGES --------------------
  useEffect(() => {
    const stateId = formData.stateId;
    if (!stateId) {
      setDistricts([]);
      return;
    }

    axiosApi
      .post("/district/getDistrict", { stateIds: [stateId] })
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.districts || [];
        setDistricts(data);
      })
      .catch((err) => {
        console.error("Failed to load districts", err);
        setDistricts([]);
      });
  }, [formData.stateId]);

  // -------------------- LOAD CITIES WHEN DISTRICT CHANGES --------------------
  useEffect(() => {
    const districtId = formData.districtId;
    if (!districtId) {
      setCities([]);
      return;
    }

    axiosApi
      .post("/city/getCity", { districtIds: [districtId] })
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.cities || [];
        setCities(data);
      })
      .catch((err) => {
        console.error("Failed to load cities", err);
        setCities([]);
      });
  }, [formData.districtId]);

  // -------------------- LOAD INITIAL HOSTELER DATA --------------------
  useEffect(() => {
    if (!hosteler) return;

    setFormData({
      email: hosteler.email || "",
      countryId: hosteler.countryId || "",
      stateId: hosteler.stateId || "",
      districtId: hosteler.districtId || "",
      cityId: hosteler.cityId || "",
      address: hosteler.address || "",
      guardianName: hosteler.guardianName || "",
      guardianContactNumber: hosteler.guardianContactNumber || "",
      dob: hosteler.dob ? hosteler.dob.split("T")[0] : "",
    });

    // If backend provided districtId or stateId / countryId, prefetch dependent lists.
    const fetchPrefetch = async () => {
      try {
        if (hosteler.countryId) {
          const resStates = await axiosApi.post("/state/getState", {
            countryIds: [hosteler.countryId],
          });
          const s = Array.isArray(resStates.data)
            ? resStates.data
            : resStates.data.states || [];
          setStates(s);
        }
        if (hosteler.stateId) {
          const resDistricts = await axiosApi.post("/district/getDistrict", {
            stateIds: [hosteler.stateId],
          });
          const d = Array.isArray(resDistricts.data)
            ? resDistricts.data
            : resDistricts.data.districts || [];
          setDistricts(d);
        }
        if (hosteler.districtId) {
          const resCities = await axiosApi.post("/city/getCity", {
            districtIds: [hosteler.districtId],
          });
          const c = Array.isArray(resCities.data)
            ? resCities.data
            : resCities.data.cities || [];
          setCities(c);
        }
      } catch (err) {
        console.error("Prefetch dependent lists failed", err);
      }
    };

    fetchPrefetch();
  }, [hosteler]);

  // -------------------- VALIDATION --------------------
  const validate = () => {
    let temp = {};
    let isValid = true;

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      temp.email = "Enter a valid email address";
      isValid = false;
    } else {
      temp.email = "";
    }

    if (!formData.guardianContactNumber.match(/^[0-9]{10}$/)) {
      temp.guardianContactNumber = "Enter a valid 10-digit phone number";
      isValid = false;
    } else {
      temp.guardianContactNumber = "";
    }

    setErrors(temp);
    return isValid;
  };

  // -------------------- SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      hostelerDTO: {
        id: hosteler.id,
        ...formData,
      },
    };

    try {
      await axiosApi.post("/hosteler/updateHostelerProfile", payload);

      setBanner({
        open: true,
        type: "success",
        message: "Profile updated successfully!",
      });
    } catch (err) {
      setBanner({
        open: true,
        type: "error",
        message: err?.response?.data?.message || "Failed to update",
      });
    }
  };

  if (!hosteler) return null;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Update Profile</DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
        {/* BANNER */}
        <Collapse in={banner.open}>
          <Alert severity={banner.type} sx={{ mb: 2 }}>
            {banner.message}
          </Alert>
        </Collapse>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* NON-EDITABLE */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Your Basic Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                disabled
                value={`${hosteler.hostlerFirstName || ""} ${hosteler.hostlerLastName || ""}`}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Mobile Number" fullWidth disabled value={hosteler.mobileNumber || ""} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Hostel" fullWidth disabled value={hosteler.hostelName || ""} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Room Type" fullWidth disabled value={hosteler.roomType || ""} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Check-in Date"
                fullWidth
                disabled
                value={hosteler.hostelJoinDate ? hosteler.hostelJoinDate.split("T")[0] : ""}
              />
            </Grid>

            {/* EDITABLE */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Editable Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                size="small"
              />
            </Grid>

            {/* DOB */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date of Birth"
                name="dob"
                type="date"
                fullWidth
                value={formData.dob}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            {/* COUNTRY */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select name="countryId" value={formData.countryId} onChange={handleChange} label="Country">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* STATE */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select name="stateId" value={formData.stateId} onChange={handleChange} label="State">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* DISTRICT */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select name="districtId" value={formData.districtId} onChange={handleChange} label="District">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {districts.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.districtName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* CITY */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>City</InputLabel>
                <Select name="cityId" value={formData.cityId} onChange={handleChange} label="City">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.cityName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ADDRESS */}
            <Grid item xs={12}>
              <TextField
                label="Address"
                name="address"
                fullWidth
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
                size="small"
              />
            </Grid>

            {/* PARENT DETAILS */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Parent Name"
                name="guardianName"
                fullWidth
                value={formData.guardianName}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Parent Phone Number"
                name="guardianContactNumber"
                fullWidth
                value={formData.guardianContactNumber}
                onChange={handleChange}
                error={Boolean(errors.guardianContactNumber)}
                helperText={errors.guardianContactNumber}
                size="small"
              />
            </Grid>

            {/* BUTTONS */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                <Button variant="contained" type="submit">
                  Update
                </Button>

                <Button variant="outlined" onClick={onClose}>
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StudentHostlerUpdate;
